import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';

export interface ISourceBrandLocationDetailsData {
  id: number;
  parentId: number | null;
  sourcePlatform: string;
  sourceExternalId: string;
  sourceUrl: string | null;
  name: string;
  address: string | null;
  coordinates: {
    lat: number | null;
    lng: number | null;
  };
  rating: number | null;
  ratingsTotal: number | null;
  reviewsTotal: number | null;
  priceLevel: number | null;
  avgCostPerCouple: number | null;
  avgCostCurrency: string | null;
  businessStatus: string | null;
  phoneNumber: string | null;
  website: string | null;
  isMaster: boolean;
  isVerified: boolean;
  createdAt: string;
  updatedAt: string;
  brand: {
    id: number;
    name: string;
    image: string | null;
    countryCode: string;
  };
  geoLocation: {
    id: number;
    name: string;
  } | null;
}

const endpoint = 'ums/v1/crm/management/source-brand-locations';

type TError = AxiosError;
type TQueryKey = [typeof endpoint, 'details', number];
type Options = Omit<
  UseQueryOptions<unknown, TError, ISourceBrandLocationDetailsData, TQueryKey>,
  'queryKey' | 'queryFn'
>;
type ReturnType = UseQueryResult<ISourceBrandLocationDetailsData, TError>;

export const useGetSourceBrandLocationByIdApi = (id: number, options?: Options): ReturnType => {
  const isLoggedIn = useIsLoggedIn();
  const toast = useCustomToast();
  const { ...otherOptions } = options || {};

  const queryFn = async () => {
    try {
      const { data } = await http.get(`${endpoint}/${id}`);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper([endpoint, 'details', id], queryFn, {
    enabled: isLoggedIn && !!id,
    ...otherOptions,
  });
};
